import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Store } from "@ngxs/store";
import { BaseModalComponent } from "@ha/ui/common";
import { CountersignRole } from "@ha/data/basic";
import { SelectionListItem } from "@ha/ui/forms";
import { NgSignaturePadOptions, SignaturePadComponent } from "@almothafar/angular-signature-pad";
import { PermitTeamActions } from "../../states/permit-team.action";
import { ResetForm } from "@ngxs/form-plugin";

@UntilDestroy()
@Component({
    templateUrl: "add-countersign-modal.component.html",
    styleUrls: ["add-countersign-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddCountersignModalComponent extends BaseModalComponent {
    @ViewChild("signature")
    public signaturePad: SignaturePadComponent;
    
    public data: { permitId: number };

    public get permitId(): number {
        return this.data?.permitId;
    }
    
    public signaturePadOptions: NgSignaturePadOptions = undefined;
    
    public form: UntypedFormGroup; 
    
    public hasSignature = false;

    public CountersignRole = CountersignRole;

    public roles: SelectionListItem[] = [
        { value: CountersignRole.FacilityManager, langKey: "permit.countersign.roles.facilitymanager", isDisabled: false },
        { value: CountersignRole.WorkEnvironmentManager, langKey: "permit.countersign.roles.workenvironmentmanager", isDisabled: false },
        { value: CountersignRole.BAS_U, langKey: "permit.countersign.roles.BAS_U", isDisabled: false },
        { value: CountersignRole.Client, langKey: "permit.countersign.roles.client", isDisabled: false },
        { value: CountersignRole.FireSafetyManager, langKey: "permit.countersign.roles.firesafetymanager", isDisabled: false },
        { value: CountersignRole.FireSafetyCoordinator, langKey: "permit.countersign.roles.firesafetycoordinator", isDisabled: false },
        { value: CountersignRole.OperationsManager, langKey: "permit.countersign.roles.operationsmanager", isDisabled: false },
        { value: CountersignRole.PropertyOwner, langKey: "permit.countersign.roles.propertyowner", isDisabled: false },
        { value: CountersignRole.SecurityManager, langKey: "permit.countersign.roles.securitymanager", isDisabled: false },
        { value: CountersignRole.OtherRole, langKey: "permit.countersign.roles.otherrole", isDisabled: false }
    ];

    public noScroll = false;

    constructor(private store: Store,  private actions$: Actions, private cdr: ChangeDetectorRef, private fb: UntypedFormBuilder,) {
        super();
        this.actions$.pipe(ofActionSuccessful(PermitTeamActions.CountersignPermit))
                    .pipe(untilDestroyed(this))
                    .subscribe(() => {
                        this.resetForm();
                        this.closeModal();
                    });

        this.createForm();
    }

    public ngOnInit(): void {
        this.resetForm();
        let role = this.form.get('role');
        role?.setValue(this.CountersignRole.FireSafetyManager);
        role?.valueChanges.subscribe(role => {
            const roleTextControl = this.form.get('roletext');

            if(role === CountersignRole.OtherRole) {
                roleTextControl?.setValidators([
                    Validators.required,
                    Validators.minLength(2)                    
                ]);
            } else {
                roleTextControl?.clearValidators();
                roleTextControl?.setValue("", { emitEvent: false });
            }

            roleTextControl?.updateValueAndValidity({ emitEvent: false });
        })
    }
    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.signaturePad.clear();
    }

    @HostListener("window:resize", ["$event"])
    public getScreenSize(): void {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.signaturePadOptions = {
            minWidth: 1,
            maxWidth: 4,
            backgroundColor: "#ffffff",
            canvasWidth: width,
            canvasHeight: width / 2,
        };

        this.cdr.detectChanges();
    }

    public clear() {
        this.signaturePad.clear();
        this.hasSignature = !this.signaturePad.isEmpty();
    }

     // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public drawComplete(event: MouseEvent | Touch) {
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    public drawStart(event: MouseEvent | Touch) {
    }

    private createForm() {
            this.form = this.fb.group({
                member: [{ value: "", disabled: false }, [
                    Validators.required,
                    Validators.minLength(2),
                ]],
                role: [{value: "", disabled: false }, [
                    Validators.required
                ]],
                roletext: [{ value: "", disabled: false }]
            });
        }

    public save() {
        if (this.form.valid) {
            const member = this.form.get('member')?.value;
            const role = this.form.get('role')?.value;
            const roletext = this.form.get('roletext').value;            

            this.store.dispatch(new PermitTeamActions.CountersignPermit(this.permitId, member, role, roletext, this.signaturePad.toDataURL()));
        } else {
            this.form.markAsDirty();
        }

        this.resetForm();
        this.closeModal();
    }

    private resetForm(): void {
        this.store.dispatch(new ResetForm({
            path: 'permitTeam.newPermitTeamTextMemberForm'
        }));

        this.form.reset(
            {
                member: "",
                role: "",
                roletext: ""
            },
            { emitEvent: false }
        );
    }

    public close() {   
        this.resetForm();     
        this.closeModal();
    }

    public get isValid() {
        return this.hasSignature && this.form.valid;
    }

    public abortClear(): void {         
        this.close();
    }

    public continue(): void {
        this.noScroll = false;
    }
}